(ns ui.view.survey.form
    (:require
     [clojure.edn :as edn]
     [clojure.string :as string]
     [common.localization :as loc]
     [common.util :as cu]
     [frontend.utils :as fu]
     [re-frame.core :as rf]
     [reagent.core :as r]
     [ui.components.button.view :as button]
     [ui.components.ck-editor.view :as ck-editor]
     [ui.components.radix-ui.popover.view :as popover]
     [ui.components.toggle.view :as toggle]
     [ui.editors.data-input :as data-input]
     [ui.editors.file.event :as upload-event]
     [ui.editors.file.upload :as upload]
     [ui.state.session.sub :as session-sub]
     [ui.util :as uu]
     [ui.view.survey.components :as components]
     [ui.view.survey.event :as event]
     [ui.view.survey.modals.confirm-overwrite-answers :as overwrite-existing-answers-modal]
     [ui.view.survey.modals.confirm-remove-column :as confirm-remove-row-modal]
     [ui.view.survey.modals.confirm-submit :as confirm-submit-modal]
     [ui.view.survey.modals.loading-excel :as loading-excel-modal]
     [ui.view.survey.modals.mandatory-missing :as mandatory-missing-modal]
     [ui.view.survey.modals.missing-sheets :as missing-sheets-modal]
     [ui.view.survey.modals.num-fmt-select :as num-fmt-modal]
     [ui.view.survey.modals.resolve-errors :as resolve-errors-modal]
     [ui.view.survey.modals.select-sheet :as select-sheet-modal]
     [ui.view.survey.modals.tesi-esg-errors :as tesi-errors-modal]
     [ui.view.survey.sub :as sub]
     [ui.view.survey.timeseries-table :as timeseries-table]))

(defn wysiwyg-block
  [block]
  [:div
   {:class "flex-auto content-stretch"}
   [:div
    {:class "mb-4 flex flex-col"}
    [:div
     {:class "flex flex-col w-full"}
     (when-not (cu/empty-value? block)
       [:h2
        {:class "text-helper-light-text text-lg font-semibold bg-helper-light px-4 py-2"}
        (:label block)])
     [:div
      {:class "flex-auto"}
      [:div
       {:class "p-3 mb-0 text-sm text-complement-regular-text border border-solid border-base-light-border-paler rounded"}
       ^{:key (:content block)}
       [ck-editor/viewer (:content block)]]]]]])

(defn info-icon
  [{:keys [_survey-id _survey-question-id _info _label show-question-ext-ids? question-ext-id-labels]}]
  (let [state (r/atom {})]
    (fn [{:keys [survey-id survey-question-id info label]}]
      [popover/view
       {:open?          (:open? @state)
        :on-open-change (fn [_]
                          (swap! state update :open? not))}
       [:i
        {:class    "fas fa-info-circle text-base-light-text-paler hover:text-base-light-text-accent"
         :on-click (fn [_e]
                     (rf/dispatch [::event/show-info survey-id survey-question-id]))}]

       [:div
        {:class "flex flex-col"}
        [:div
         {:class "flex-1 flex flex-row justify-end max-w-[80vw] sm:max-w-[400px] md:max-w-[600px] lg:max-w-[800px]"}
         [ck-editor/viewer
          (str "<p><strong>" (string/upper-case label) "</strong></p>"
               (if (string/starts-with? info "<")
                 info
                 (str "<p>" info "</p>")))]
         [:i
          {:class    "fas fa-times-circle"
           :on-click (fn [_]
                       (swap! state assoc :open? false))}]]
        (when show-question-ext-ids?
          [:div
           {:class "text-xs text-complement-regular-text font-thin"}
           question-ext-id-labels])]])))

(defn calculated-question
  [{:keys [value precision]}]
  [:div
   {:class "px-4 py-2 text-complement-light-text border border-complement-light-border bg-complement-light"}
   [:i
    {:class "fal fa-calculator"}]
   " "
   (fu/round value (or precision 0))])

(defn boolean-question
  [{:keys [survey-id no-answer? survey-question-id value lang]}]
  [:div
   {:class "flex flex-row gap-2 group"}
   [:label
    {:class "flex items-center gap-2 pointer"}
    [:input
     {:type      "radio"
      :checked   (true? value)
      :on-focus  (fn [_]
                   (rf/dispatch [::event/show-info survey-id nil]))
      :on-change (fn [_]
                   (rf/dispatch [::event/on-answer-input survey-id 0 survey-question-id true nil nil no-answer?]))
      :name      (str "survey-question-" survey-id "-" survey-question-id)}]
    (loc/tr :yes nil lang)]
   [:label
    {:class "flex items-center gap-2 pointer"}
    [:input
     {:type      "radio"
      :checked   (false? value)
      :on-focus  (fn [_]
                   (rf/dispatch [::event/show-info survey-id nil]))
      :on-change (fn [_]
                   (rf/dispatch [::event/on-answer-input survey-id 0 survey-question-id false nil nil no-answer?]))
      :name      (str "survey-question-" survey-id "-" survey-question-id)}]
    (loc/tr :no nil lang)]

   (when (boolean? value)
     [:label
      {:class    "hidden items-center gap-2 text-danger-light-text pointer group-hover:flex flex-row"
       :on-click (fn [_]
                   (rf/dispatch [::event/on-answer-input survey-id 0 survey-question-id nil nil nil no-answer?]))}
      [:span
       [:i.fal.fa-times-circle]]
      (loc/tr :clear nil lang)])

   #_[:label
      {:class "flex items-center gap-2 pointer"}
      [:input
       {:type      "radio"
        :checked   (nil? value)
        :on-change (fn [_]
                     (rf/dispatch [::event/on-answer-input survey-id 0 survey-question-id nil nil nil]))
        :name      (str "survey-question-" survey-id "-" survey-question-id)}]
      (loc/tr :not-set nil lang)]])

(defn file-item
  [{:keys [survey-id survey-question-id file-id multi-file? file-name]}]
  [:div
   {:class "flex flex-row gap-2 align-baseline group"}
   [:i
    {:class "flex-0 fal fa-file"}]
   [:span
    {:class "flex-1"}
    file-name]
   (when (or (some #{@(rf/subscribe [::session-sub/role])} [:role/user :role/admin :role/zadmin])
             (some #{file-id} @(rf/subscribe [::sub/my-file-ids])))
     [button/secondary-open
      {:on-click (fn [_]
                   (rf/dispatch [::event/download-file {:survey-id          survey-id
                                                        :survey-question-id survey-question-id
                                                        :file-id            file-id}]))}
      [:i
       {:class "fas fa-download hidden cursor-pointer group-hover:block"}]])
   [button/secondary-open
    {:on-click (fn [_]
                 (rf/dispatch [::event/on-answer-input-file {:survey-id          survey-id
                                                             :answer-series      0
                                                             :survey-question-id survey-question-id
                                                             :file-id            file-id
                                                             :multi-file?        multi-file?
                                                             :remove?            true
                                                             :file-name          file-name}]))}
    [:i
     {:class "fal fa-trash-alt hidden cursor-pointer group-hover:block"}]]])

(defn file-question
  [{:keys [_value _lang _disabled? _read-only? _survey-id _survey-question-id]}]
  (let [state* (r/atom {})]
    (fn [{:keys [survey-question/multi-file? survey-question/accepted-file-types value disabled? read-only? survey-id survey-question-id]}]
      (let [files @(rf/subscribe [::sub/answer-files survey-id value multi-file?])
            file-ctx {:survey-id          survey-id
                      :survey-question-id survey-question-id
                      :multi-file?        multi-file?}]
        [:<>
         (when (and value
                    (or (not multi-file?)
                        (seq value)))
           [:ul
            {:class "flex flex-col gap-2"}
            (if-not multi-file?
              [file-item (assoc file-ctx :file-id value
                                :file-name files)]
              (->> value
                   (map (fn [file-id]
                          ^{:key (str "survey-response-" file-id)}
                          [file-item (assoc file-ctx :file-id file-id
                                            :file-name (get files file-id))]))
                   (doall)))])
         [upload/indicators state*]
         (when (or multi-file?
                   (not files))
           [data-input/view
            {:data-type         :data.type/file
             :multi-file?       multi-file?
             :accept-file-types accepted-file-types
             :class             "w-full"
             :full-width?       true
             :show-clear?       true
             :value             (if disabled?
                                  nil
                                  value)
             :date-format       "dd.MM.yyyy"
             :disabled?         (or read-only? disabled?)
             :style-opts        {:container-height "auto"}
             :on-change         (fn [new-value]
                                  (doseq [file (:files new-value)]
                                    (upload-event/start-upload! {:state*    state*
                                                                 :target-id survey-id
                                                                 :data      {:survey-id          survey-id
                                                                             :survey-question-id survey-question-id
                                                                             :update-type        :update.type/append}
                                                                 :error!    (fn [resp]
                                                                              (js/console.log "Failed to upload file : " resp))
                                                                 :success!  (fn [{:keys [db/id attachment/filename]}]
                                                                              (rf/dispatch [::event/on-answer-input-file {:survey-id          survey-id
                                                                                                                          :answer-series      0
                                                                                                                          :survey-question-id survey-question-id
                                                                                                                          :file-id            id
                                                                                                                          :multi-file?        multi-file?
                                                                                                                          :remove?            false
                                                                                                                          :file-name          filename}]))}
                                                                file))
                                  #_(try (rf/dispatch [::event/on-answer-input survey-id 0 survey-question-id new-value nil nil])
                                         (catch js/Object o
                                           (js/console.log "onValuechange error : " o))))}])]))))

(defn default-question
  [{:keys [survey-question/data-type value lang no-answer? options number-format disabled? read-only? precision survey-id survey-question-id]}]
  (let [option-type? (some #{data-type} [:data.type/keyword :data.type/ref-single :data.type/ref-multi :data.type/boolean
                                         :data.type/keyword-multi])
        single-type? (some #{data-type} [:data.type/keyword :data.type/ref-single :data.type/boolean])
        resolved-options (concat (when-not (cu/blank-value? value)
                                   [{:value nil
                                     :label (loc/tr :clear-select-option-label nil lang)}])
                                 (if (= data-type :data.type/boolean)
                                   [{:value true
                                     :label (loc/tr :yes nil lang)}
                                    {:value false
                                     :label (loc/tr :no nil lang)}]
                                   (->> options
                                        (sort-by :common/ord)
                                        (map (fn [opt]
                                               (let [label (:label opt)]
                                                 (if (string? label)
                                                   opt
                                                   (assoc opt :label (cond (map? label)
                                                                           (or (get label lang)
                                                                               (get label :ENG)))))))))))
        resolved-value (cond (and option-type? single-type?)
                             (->> resolved-options
                                  (filter (fn [opt]
                                            (some #{value} [(:value opt) (pr-str (:value opt))])))
                                  (first))
                             option-type?
                             (->> resolved-options
                                  (filter (fn [opt]
                                            (some #{(:value opt) (pr-str (:value opt))} value))))
                             (= data-type :data.type/date)
                             (if (string? value)
                               (edn/read-string value)
                               value)
                             :else
                             value)]
    [data-input/view
     {:editor-type   (case data-type
                       :data.type/boolean :editor.type/select
                       nil)
      :data-type     (case data-type
                       :data.type/boolean :data.type/keyword
                       data-type)
      :number-format number-format
      :class         "w-full"
      :full-width?   true
      :show-clear?   true
      :placeholder   (if (some #{data-type} [:data.type/ref-single :data.type/ref-multi :data.type/keyword :data.type/keyword-multi])
                       (loc/tr :select-placeholder nil lang)
                       (loc/tr data-type nil lang))
      :value         (if disabled?
                       nil
                       resolved-value)
      :options       (->> resolved-options
                          (map (fn [opt]
                                 (assoc opt :value (pr-str (:value opt))))))
      :date-format   "dd.MM.yyyy"
      :disabled?     (or read-only? disabled?)
      :style-opts    {:container-height "auto"}
      :max-decimals  precision
      :on-change     (fn [new-value]
                       (let [new-val (cond (and option-type? single-type?)
                                           (edn/read-string new-value)
                                           option-type?
                                           (->> new-value
                                                (mapv (fn [v]
                                                        (if (string? v)
                                                          (edn/read-string v)
                                                          v))))
                                           :else
                                           new-value)]
                         (try (rf/dispatch [::event/on-answer-input survey-id 0 survey-question-id new-val nil nil no-answer?])
                              (catch js/Object o
                                (js/console.log "onValuechange error : " o)))))}]))

(def ^:private max-previous-answer-chars 150)

(defn- previous-answer-display
  "Displays previous answer with proper handling for different value types.
   - text-multiline and text-richtext are rendered as components (hiccup)
   - Other types are plain strings with optional truncation"
  [_props]
  (let [expanded? (r/atom false)]
    (fn [{:keys [previous-value data-type]}]
      (when previous-value
        (let [;; These data types return hiccup/component from formatted-display-string
              renders-as-component? (contains? #{:data.type/text-multiline
                                                 :data.type/text-richtext}
                                               data-type)
              ;; For plain strings, check length for truncation
              needs-truncation? (and (string? previous-value)
                                     (> (count previous-value) max-previous-answer-chars))
              display-value (if (and needs-truncation? (not @expanded?))
                              (str (subs previous-value 0 max-previous-answer-chars) "...")
                              previous-value)]
          [:div
           {:class "text-complement-light-text text-xs mt-1"}
           [:span (loc/tr :previous-answer)]
           (if renders-as-component?
             ;; Render hiccup/component directly
             [:div {:class "inline"} previous-value]
             ;; Plain string with possible truncation
             [:span (str display-value)])

           ;; Show expand/collapse button for long text
           (when needs-truncation?
             [:button
              {:class    "ml-1 text-primary-light-text hover:underline cursor-pointer"
               :on-click #(swap! expanded? not)}
              (if @expanded?
                (loc/tr :show-less)
                (loc/tr :show-more))])])))))

(defn question-view
  [survey-id {:keys [survey-question-id label show-mandatoriness? precision
                     survey-question/data-unit survey-question/data-type
                     value previous-value calculated? info] :as question}]
  (let [lang @(rf/subscribe [::sub/selected-lang survey-id])
        number-format @(rf/subscribe [::sub/num-fmt])
        question-mandatory? @(rf/subscribe [::sub/question-mandatory? survey-id 0 question])
        show-question-ext-ids? @(rf/subscribe [::sub/show-questions-ext-ids? survey-id])
        question-ext-id-labels @(rf/subscribe [::sub/survey-question-ext-id-labels survey-id question])]
    [:<>
     [:div
      {:class "flex justify-between gap-2"}
      [:div
       {:class-name "flex flex-col justify-center"}
       [:label
        {:class "mb-0 flex flex-row gap-1 leading-3 font-bold text-sm leading-5 "}
        label
        (when (and question-mandatory? show-mandatoriness?)
          [:span
           {:class "text-danger-light-text"}
           "*"])]
       [:div
        (when-not (cu/blank-value? data-unit)
          [:span
           {:class "pt-0.5 flex gap-0.5 items-center text-xs leading-3 text-complement-light-text font-normal"}
           [:strong (str "(" data-unit ")")]])]]

      [:div
       (when-not (cu/blank-value? info)
         [info-icon
          {:survey-id              survey-id
           :survey-question-id     survey-question-id
           :info                   info
           :label                  label
           :show-question-ext-ids? show-question-ext-ids?
           :question-ext-id-labels question-ext-id-labels}])]]
     (cond calculated?
           [calculated-question {:data-type data-type
                                 :value     value
                                 :precision precision}]
           (= data-type :data.type/boolean)
           [boolean-question (assoc question :survey-id survey-id
                                    :lang lang)]
           (= data-type :data.type/file)
           [file-question (assoc question :survey-id survey-id)]
           :else
           [default-question (assoc question :survey-id survey-id
                                    :number-format number-format
                                    :lang lang)])
     [previous-answer-display {:previous-value previous-value
                               :data-type      data-type}]]))

(defn section-item
  [survey-id {:keys [survey-item-type cannot-skip? content survey-question-id
                     survey-question/data-type survey-question/options] :as content-item}]
  (let [value @(rf/subscribe [::sub/question-value survey-id survey-question-id content-item])
        lang @(rf/subscribe [::sub/selected-lang])
        config @(rf/subscribe [::sub/survey-config survey-id])
        dom-id (when (= :survey-item/question survey-item-type)
                 (sub/dom-id survey-id 0 survey-question-id))
        no-answer? @(rf/subscribe [::sub/question-no-answer? survey-id survey-question-id])
        visible? @(rf/subscribe [::sub/question-visible? survey-id 0 content-item])
        show-missing? @(rf/subscribe [::sub/show-missing-question? survey-id 0 survey-question-id])
        previous-answer-data @(rf/subscribe [::sub/question-previous-answer survey-id survey-question-id])
        previous-value (when (and previous-answer-data
                                  (:exists? previous-answer-data)
                                  (not= data-type :data.type/calculated-number))
                         (let [raw-value (:value previous-answer-data)]
                           (when (and (some? raw-value)
                                      (not (and (string? raw-value) (cu/blank-value? raw-value))))
                             (data-input/formatted-display-string
                               {:data-type   data-type
                                :value       raw-value
                                :options     options
                                :lang        lang
                                :date-format (loc/tr :date-format)}))))]
    (when visible?
      [:li
       (merge {:class (fu/cs "p-2 flex flex-row gap-2 relative rounded "
                             (if (and show-missing? (cu/blank-value? value))
                               "bg-danger-light"
                               "hover:bg-base-regular"))}
              (when dom-id
                {:id dom-id}))
       (when (and false
                  (not (:hide-no-answer? config)))
         [:div
          {:class (uu/cm "flex flex-row gap-1 shrink-0 grow-0 flex-col"
                         (when (= :survey-item/wysiwyg survey-item-type)
                           "opacity-0"))}
          [:div
           {:class (uu/cm "flex-auto flex flex-row")}
           [:label
            {:class "w-12 flex flex-auto flex-row self-end content-center text-xs text-complement-light-text"}
            (cond cannot-skip?
                  ""
                  no-answer?
                  (loc/tr :toggle-to-not-answer-survey-question nil lang)
                  (cu/empty-value? value)
                  (loc/tr :toggle-to-answer-enabled-empty-value nil lang)
                  :else
                  (loc/tr :toggle-to-answer-enabled-with-value nil lang))]]
          [:div
           {:class "w-10 flex flex-row grow-0 shrink-0 content-center"}
           (when-not cannot-skip?
             [toggle/toggle
              {:checked?  (not no-answer?)
               :on-change #(rf/dispatch [::event/on-answer-no-response survey-id survey-question-id (not no-answer?)])}])]])
       [:div
        {:class "flex flex-col flex-auto gap-1"}
        (case survey-item-type
          :survey-item/wysiwyg
          [ck-editor/viewer content]
          :survey-item/question
          [question-view
           survey-id
           (assoc content-item
                  :no-answer? no-answer?
                  :value value
                  :previous-value previous-value
                  :disabled? no-answer?)]
          (str "Unknown survey-item-type " survey-item-type))]])))

(defn section-block
  [survey-id block]
  (let [lang @(rf/subscribe [::sub/selected-lang])
        visible-items (->> block
                           :content
                           (map-indexed (fn [idx item]
                                          (section-item survey-id (assoc item :last? (= idx (dec (count (:content block))))))))
                           (remove nil?)
                           (seq))]
    [:div
     {:class "flex-auto content-stretch"}
     [:div
      {:class "mb-4 flex flex-col"}
      [:div
       {:class "flex flex-col w-full"}
       [:h2
        {:class "text-helper-light-text text-lg font-semibold bg-helper-light px-4 py-2"}
        (:label block)]
       [:div
        {:class "md:py-3 text-base-light-text"}
        (if visible-items
          (into
            [:ul
             {:class "flex flex-1 gap-1 flex-col"}]
            visible-items)
          [:div
           {:class ""}
           (loc/tr :no-questions nil lang)])]]]]))

(defn table-block
  [{:keys [survey-id thousand-separator decimal-separator lang number-format block]}]
  (let [table-data @(rf/subscribe [::sub/table-values survey-id lang block])]
    [:div
     (when-let [label (:label block)]
       [:h2
        {:class "text-helper-light-text text-lg font-semibold bg-helper-light px-4 py-2"}
        label])
     [timeseries-table/view
      {:survey-id            survey-id
       :number-format        number-format
       :thousand-separator   thousand-separator
       :decimal-separator    decimal-separator
       :col-series-min-width 120
       :question-min-width   200
       :columns              (:columns block)
       :questions            (:content block)
       :questions-lookup     (:questions-lookup block)
       :lang                 lang
       :table-data           table-data}]]))

(defn form-body
  [survey-id]
  (try
    (let [configuration @(rf/subscribe [::sub/survey-config survey-id])
          lang @(rf/subscribe [::sub/selected-lang survey-id])
          number-format @(rf/subscribe [::sub/num-fmt])
          thousand-separator @(rf/subscribe [::sub/thousand-separator])
          decimal-separator @(rf/subscribe [::sub/decimal-separator])
          uploading? @(rf/subscribe [::sub/uploading? survey-id])]
      (into
        ^{:key (str "uploading? : " uploading?)}
        [:div
         {:class "flex shrink-0 grow-0 flex-col content-stretch gap-2"}]
        (->> configuration
             :blocks
             (keep (fn [{:keys [block-type] :as block}]
                    ;(js/console.log "Block : " block)
                     (case block-type
                       :survey-block/wysiwyg
                       [wysiwyg-block block]
                       :survey-block/section
                       [section-block survey-id block]
                       :survey-block/table-block
                       [table-block {:lang               lang
                                     :number-format      number-format
                                     :thousand-separator thousand-separator
                                     :decimal-separator  decimal-separator
                                     :survey-id          survey-id
                                     :block              block}]
                       nil)))
             (doall))))
    (catch js/Object e
      (js/console.error e)
      [:div
       {:class ""}
       "err"])))

(defn form-header
  [survey-id]
  (let [resolved-survey-label @(rf/subscribe [::sub/survey-label-display survey-id])
        lang @(rf/subscribe [::sub/selected-lang survey-id])
        hero-img-url @(rf/subscribe [::sub/hero-img-url survey-id])
        target-name-if-not-in-template @(rf/subscribe [::sub/target-name-if-not-in-label survey-id])
        access-to-other-surveys? @(rf/subscribe [::sub/access-to-other-surveys?])
        numeric-questions? @(rf/subscribe [::sub/numeric-questions? survey-id])]
    [:<>
     [:div
      {:class "p-3"}
      [:div
       {:class "pt-2 pb-3 flex items-center gap-2"}
       (when hero-img-url
         [:img
          {:class "max-h-[40px]"
           :src   hero-img-url}])
       (when access-to-other-surveys?
         [:div
          {:class-name "flex items-center flex-auto justify-end gap-5"}
          [:span
           {:class-name "flex flex-row gap-1 pointer text-complement-light-text text-semibold align-center"
            :on-click   (fn [_]
                          (rf/dispatch [::event/re-select-survey]))}
           (loc/tr :switch-to-another-survey nil lang)]])]]
     [:div
      {:class "px-3 pb-2 flex items-center gap-2 justify-between border-b border-solid border-complement-light-border"}
      [:div
       {:class "flex flex-col gap-2"}
       [:div
        {:class-name "flex items-center gap-2"}
        [:div
         {:class (fu/cs "text-xl font-bold flex-auto text-complement-light-text")}
         resolved-survey-label]]
       (when target-name-if-not-in-template
         [:div
          {:class-name "font-bold"}
          target-name-if-not-in-template])
       [components/reporting-period-text survey-id lang]]
      [:div
       {:class-name "flex items-end flex-auto justify-end gap-5"}
       [components/tesi-company-excel-block survey-id]
       (when numeric-questions?
         [:div
          {:class-name "hidden md:flex"}
          [components/thousand-separator]])
       (when numeric-questions?
         [:div
          {:class-name "hidden md:flex"}
          [components/decimal-separator]])
       [components/lang-selector survey-id lang]]]]))

(defn view
  [survey-id]
  (let [tables? @(rf/subscribe [::sub/tables-in-config? survey-id])]                                ;; TODO:
    [:div
     {:class (fu/cs "p-0 flex flex-col flex-1 gap-2"
                    (when-not tables?
                      "container max-w-[1024px]"))}
     [form-header survey-id]
     [:div
      {:class-name "p-3"}
      [form-body survey-id]
      [select-sheet-modal/view]
      [resolve-errors-modal/view]
      [tesi-errors-modal/view]
      [missing-sheets-modal/view]
      [overwrite-existing-answers-modal/view]
      [confirm-remove-row-modal/view]
      [confirm-submit-modal/view]
      [num-fmt-modal/view]
      [loading-excel-modal/view survey-id]
      [mandatory-missing-modal/view survey-id]]]))
